import { color as echartsColor } from "echarts";
import {
  assertValidLiveHeatmapConfig,
  type LiveHeatmapConfig,
} from "@/features/deployment-dashboard/live-view/config/live-heatmap-config";
import type { LiveHeatmapLayer, LiveTrackPoint } from "@/features/deployment-dashboard/types";
import {
  worldToFloorplanPixel,
  type WorldToPixelTransformAsset,
} from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";

const colorLookupCache = new WeakMap<LiveHeatmapConfig, Uint8ClampedArray>();
const rasterBufferCache = new WeakMap<HTMLCanvasElement, LiveHeatmapRasterBuffer>();

interface LiveHeatmapRasterBuffer {
  width: number;
  height: number;
  density: Float32Array;
  imageData: ImageData;
}

interface HeatKernelTransform {
  scaleXx: number;
  scaleXy: number;
  scaleYx: number;
  scaleYy: number;
  translateX: number;
  translateY: number;
}

function createColorLookup(config: LiveHeatmapConfig): Uint8ClampedArray {
  const lookup = new Uint8ClampedArray(256 * 3);
  for (let index = 0; index < 256; index += 1) {
    const heatValue = (index / 255) * config.maxValue;
    const rightIndex = config.gradientStops.findIndex((stop) => stop.value >= heatValue);
    const right = config.gradientStops.at(rightIndex === -1 ? -1 : rightIndex);
    const left = config.gradientStops.at(Math.max(0, rightIndex - 1));
    if (!left || !right) continue;

    const range = right.value - left.value;
    const ratio = range === 0 ? 0 : (heatValue - left.value) / range;
    const [red, green, blue] = echartsColor.parse(
      echartsColor.lerp(ratio, [left.color, right.color])
    );
    const offset = index * 3;
    lookup.set([red, green, blue], offset);
  }
  return lookup;
}

function getColorLookup(config: LiveHeatmapConfig): Uint8ClampedArray {
  const cached = colorLookupCache.get(config);
  if (cached) return cached;

  const lookup = createColorLookup(config);
  colorLookupCache.set(config, lookup);
  return lookup;
}

function colorize(buffer: LiveHeatmapRasterBuffer, config: LiveHeatmapConfig): void {
  const colorLookup = getColorLookup(config);
  const pixels = buffer.imageData.data;
  for (let index = 0; index < buffer.density.length; index += 1) {
    const offset = index * 4;
    // The loop bounds guarantee this typed-array index is in range.
    // eslint-disable-next-line security/detect-object-injection
    const intensity = Math.round(Math.min(1, buffer.density[index]) * 255);
    if (intensity === 0) {
      pixels.fill(0, offset, offset + 4);
      continue;
    }

    const colorOffset = intensity * 3;
    // The loop-derived pixel offset is bounded by the ImageData length.
    // eslint-disable-next-line security/detect-object-injection
    pixels[offset] = colorLookup[colorOffset] ?? 0;
    pixels[offset + 1] = colorLookup[colorOffset + 1] ?? 0;
    pixels[offset + 2] = colorLookup[colorOffset + 2] ?? 0;
    pixels[offset + 3] = intensity;
  }
}

function getRasterBuffer(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  width: number,
  height: number
): LiveHeatmapRasterBuffer {
  const cached = rasterBufferCache.get(canvas);
  if (cached?.width === width && cached.height === height) return cached;

  const buffer = {
    width,
    height,
    density: new Float32Array(width * height),
    imageData: context.createImageData(width, height),
  };
  rasterBufferCache.set(canvas, buffer);
  return buffer;
}

function stampHeatKernel(
  buffer: LiveHeatmapRasterBuffer,
  transform: HeatKernelTransform,
  coreRadius: number,
  outerRadius: number,
  opacity: number
): void {
  const { scaleXx, scaleXy, scaleYx, scaleYy, translateX, translateY } = transform;
  const determinant = scaleXx * scaleYy - scaleYx * scaleXy;
  if (
    !Number.isFinite(determinant) ||
    Math.abs(determinant) < Number.EPSILON ||
    !Number.isFinite(translateX) ||
    !Number.isFinite(translateY)
  ) {
    return;
  }

  const extentX = outerRadius * Math.hypot(scaleXx, scaleYx);
  const extentY = outerRadius * Math.hypot(scaleXy, scaleYy);
  const minX = Math.max(0, Math.floor(translateX - extentX));
  const maxX = Math.min(buffer.width - 1, Math.ceil(translateX + extentX));
  const minY = Math.max(0, Math.floor(translateY - extentY));
  const maxY = Math.min(buffer.height - 1, Math.ceil(translateY + extentY));
  const blurRadius = outerRadius - coreRadius;

  for (let y = minY; y <= maxY; y += 1) {
    const offsetY = y + 0.5 - translateY;
    for (let x = minX; x <= maxX; x += 1) {
      const offsetX = x + 0.5 - translateX;
      const localX = (scaleYy * offsetX - scaleYx * offsetY) / determinant;
      const localY = (-scaleXy * offsetX + scaleXx * offsetY) / determinant;
      const distance = Math.hypot(localX, localY);
      if (distance > outerRadius) continue;

      const falloff = distance <= coreRadius ? 1 : (outerRadius - distance) / blurRadius;
      const sourceAlpha = opacity * falloff;
      const index = y * buffer.width + x;
      // The clamped x/y bounds guarantee this typed-array index is in range.
      // eslint-disable-next-line security/detect-object-injection
      const destinationAlpha = buffer.density[index];
      // eslint-disable-next-line security/detect-object-injection
      buffer.density[index] = sourceAlpha + destinationAlpha * (1 - sourceAlpha);
    }
  }
}

function clearCanvas(canvas: HTMLCanvasElement): void {
  canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
}

/**
 * Paints all tracks into one reusable map-aligned raster using the legacy heat
 * normalization, core-radius/blur relationship, minimum opacity, and gradient.
 */
export function renderLiveHeatmapLayer(
  transform: Pick<WorldToPixelTransformAsset, "matrix"> | undefined,
  tracks: LiveTrackPoint[],
  mapSize: { width: number; height: number } | undefined,
  config: LiveHeatmapConfig,
  reusableCanvas?: HTMLCanvasElement
): LiveHeatmapLayer | null {
  assertValidLiveHeatmapConfig(config);

  if (!transform || !mapSize || tracks.length === 0) {
    if (reusableCanvas) clearCanvas(reusableCanvas);
    return null;
  }

  const canvas = reusableCanvas ?? document.createElement("canvas");
  const width = Math.round(mapSize.width * config.rasterScale);
  const height = Math.round(mapSize.height * config.rasterScale);
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) return null;

  const rasterBuffer = getRasterBuffer(canvas, context, width, height);
  rasterBuffer.density.fill(0);
  const pointOpacity = Math.min(
    1,
    Math.max(config.pointWeight / config.maxValue, config.minPointOpacity)
  );
  const heatCoreRadius = config.pointRadiusInMeters * config.heatRadiusMultiplier;
  const blurRadius = heatCoreRadius * config.blurFactor;
  const outerRadius = heatCoreRadius + blurRadius;

  for (const track of tracks) {
    const centre = worldToFloorplanPixel(transform, track.worldX, track.worldY);
    const worldXUnit = worldToFloorplanPixel(transform, track.worldX + 1, track.worldY);
    const worldYUnit = worldToFloorplanPixel(transform, track.worldX, track.worldY + 1);

    stampHeatKernel(
      rasterBuffer,
      {
        scaleXx: (worldXUnit.x - centre.x) * config.rasterScale,
        scaleXy: (worldXUnit.y - centre.y) * config.rasterScale,
        scaleYx: (worldYUnit.x - centre.x) * config.rasterScale,
        scaleYy: (worldYUnit.y - centre.y) * config.rasterScale,
        translateX: centre.x * config.rasterScale,
        translateY: centre.y * config.rasterScale,
      },
      heatCoreRadius,
      outerRadius,
      pointOpacity
    );
  }

  colorize(rasterBuffer, config);
  context.putImageData(rasterBuffer.imageData, 0, 0);

  return {
    image: canvas,
    x: 0,
    y: 0,
    width: mapSize.width,
    height: mapSize.height,
  };
}
